import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, DollarSign, PieChart as PieChartIcon, 
  Users, FileText, RefreshCw, Plus, BarChart3,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import axios from 'axios';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area, 
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { useToast } from '../../../components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// BMP Color Palette
const COLORS = {
  primary: '#00B4D8',
  secondary: '#023E8A',
  dark: '#2B2D42',
  green: '#06D6A0',
  red: '#EF476F',
  yellow: '#FFD166'
};

const CHART_COLORS = ['#00B4D8', '#023E8A', '#06D6A0', '#FFD166', '#EF476F'];

const Dashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [profitTrend, setProfitTrend] = useState([]);
  const [cashflowTrend, setCashflowTrend] = useState([]);
  const [subscriptionsSummary, setSubscriptionsSummary] = useState([]);

  useEffect(() => {
    fetchAllData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchAllData(true);
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all data in parallel
      const [metricsRes, revenueRes, profitRes, cashflowRes, subsRes] = await Promise.all([
        axios.get(`${API_URL}/api/finance/dashboard/metrics`, { headers }),
        axios.get(`${API_URL}/api/finance/dashboard/revenue-trend`, { headers }),
        axios.get(`${API_URL}/api/finance/dashboard/profit-trend`, { headers }),
        axios.get(`${API_URL}/api/finance/dashboard/cashflow-trend`, { headers }),
        axios.get(`${API_URL}/api/finance/dashboard/subscriptions-summary`, { headers })
      ]);
      
      setMetrics(metricsRes.data);
      setRevenueTrend(revenueRes.data);
      setProfitTrend(profitRes.data);
      setCashflowTrend(cashflowRes.data);
      setSubscriptionsSummary(subsRes.data);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load dashboard data",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <BackButton />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 animate-spin text-[#00B4D8] mx-auto mb-4" />
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <BackButton />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42]">Financial Dashboard</h1>
          <p className="text-gray-600 mt-1">Real-time overview of your financial performance</p>
        </div>
        
        <Button
          onClick={() => fetchAllData(true)}
          disabled={refreshing}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="refresh-button"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="kpi-cards-grid">
        {/* Revenue MTD */}
        <Card className="p-5 hover:shadow-lg transition-shadow" data-testid="kpi-revenue-mtd">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-600">Revenue MTD</span>
            <DollarSign className="w-5 h-5 text-[#00B4D8]" />
          </div>
          <p className="text-2xl font-bold text-[#2B2D42]" data-testid="kpi-revenue-mtd-value">
            {formatCurrency(metrics?.revenue_mtd || 0)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            <ArrowUpRight className="w-4 h-4 text-green-600" />
            <span className="text-xs text-gray-500">Month to Date</span>
          </div>
        </Card>

        {/* Revenue YTD */}
        <Card className="p-5 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-600">Revenue YTD</span>
            <TrendingUp className="w-5 h-5 text-[#023E8A]" />
          </div>
          <p className="text-2xl font-bold text-[#2B2D42]">
            {formatCurrency(metrics?.revenue_ytd || 0)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            <ArrowUpRight className="w-4 h-4 text-green-600" />
            <span className="text-xs text-gray-500">Year to Date</span>
          </div>
        </Card>

        {/* Profit MTD */}
        <Card className="p-5 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-600">Profit MTD</span>
            <BarChart3 className="w-5 h-5 text-[#06D6A0]" />
          </div>
          <p className={`text-2xl font-bold ${(metrics?.profit_mtd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(metrics?.profit_mtd || 0)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            {(metrics?.profit_mtd || 0) >= 0 ? (
              <ArrowUpRight className="w-4 h-4 text-green-600" />
            ) : (
              <ArrowDownRight className="w-4 h-4 text-red-600" />
            )}
            <span className="text-xs text-gray-500">Month to Date</span>
          </div>
        </Card>

        {/* Active Subscriptions */}
        <Card className="p-5 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-600">Active Subscriptions</span>
            <Users className="w-5 h-5 text-[#FFD166]" />
          </div>
          <p className="text-2xl font-bold text-[#2B2D42]">
            {formatNumber(metrics?.active_subscriptions || 0)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            <span className="text-xs text-gray-500">Recurring Revenue</span>
          </div>
        </Card>

        {/* Outstanding Invoices */}
        <Card className="p-5 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-600">Outstanding</span>
            <FileText className="w-5 h-5 text-[#EF476F]" />
          </div>
          <p className="text-2xl font-bold text-[#2B2D42]">
            {formatNumber(metrics?.outstanding_invoices || 0)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            <span className="text-xs text-gray-500">Unpaid Invoices</span>
          </div>
        </Card>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
          <p className="text-sm font-semibold text-gray-600 mb-1">Cash Flow MTD</p>
          <p className={`text-xl font-bold ${(metrics?.cashflow_mtd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(metrics?.cashflow_mtd || 0)}
          </p>
        </Card>

        <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50">
          <p className="text-sm font-semibold text-gray-600 mb-1">Profit YTD</p>
          <p className={`text-xl font-bold ${(metrics?.profit_ytd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(metrics?.profit_ytd || 0)}
          </p>
        </Card>

        <Card className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50">
          <p className="text-sm font-semibold text-gray-600 mb-1">90-Day Forecast</p>
          <p className="text-xl font-bold text-[#2B2D42]">
            {formatCurrency(metrics?.forecast_90_days || 0)}
          </p>
        </Card>

        <Card className="p-4 bg-gradient-to-r from-purple-50 to-pink-50">
          <p className="text-sm font-semibold text-gray-600 mb-1">Paid Invoices</p>
          <p className="text-xl font-bold text-green-600">
            {formatNumber(metrics?.paid_invoices || 0)}
          </p>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Revenue Trend */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-[#2B2D42] mb-4">Revenue Trend (12 Months)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" stroke="#666" style={{ fontSize: '12px' }} />
              <YAxis stroke="#666" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                formatter={(value) => formatCurrency(value)}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke={COLORS.primary} 
                strokeWidth={3}
                dot={{ fill: COLORS.primary, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Profit vs Costs */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-[#2B2D42] mb-4">Profit vs Costs (12 Months)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={profitTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" stroke="#666" style={{ fontSize: '12px' }} />
              <YAxis stroke="#666" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                formatter={(value) => formatCurrency(value)}
              />
              <Legend />
              <Bar dataKey="revenue" fill={COLORS.primary} name="Revenue" />
              <Bar dataKey="costs" fill={COLORS.red} name="Costs" />
              <Bar dataKey="profit" fill={COLORS.green} name="Profit" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Cash Flow Trend */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-[#2B2D42] mb-4">Cash Flow (12 Months)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={cashflowTrend}>
              <defs>
                <linearGradient id="colorCashflow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={COLORS.green} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" stroke="#666" style={{ fontSize: '12px' }} />
              <YAxis stroke="#666" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                formatter={(value) => formatCurrency(value)}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="cashflow" 
                stroke={COLORS.green} 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorCashflow)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Subscriptions Breakdown */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-[#2B2D42] mb-4">Subscriptions Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={subscriptionsSummary}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ status, count }) => `${status}: ${count}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {subscriptionsSummary.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="p-6" data-testid="quick-actions-panel">
        <h3 className="text-lg font-bold text-[#2B2D42] mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="quick-actions-grid">
          <Button
            onClick={() => navigate('/admin/finance/invoices')}
            className="bg-gradient-to-r from-[#00B4D8] to-[#023E8A] hover:from-[#0096c7] hover:to-[#012a5e] text-white"
            data-testid="quick-action-create-invoice"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Invoice
          </Button>

          <Button
            onClick={() => navigate('/admin/finance/subscriptions')}
            className="bg-gradient-to-r from-[#023E8A] to-[#00B4D8] hover:from-[#012a5e] hover:to-[#0096c7] text-white"
            data-testid="quick-action-create-subscription"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Subscription
          </Button>

          <Button
            onClick={() => navigate('/admin/finance/reports')}
            className="bg-gradient-to-r from-[#06D6A0] to-[#00B4D8] hover:from-[#05b88a] hover:to-[#0096c7] text-white"
            data-testid="quick-action-view-reports"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            View Reports
          </Button>

          <Button
            onClick={() => navigate('/admin/finance/reports')}
            className="bg-gradient-to-r from-[#FFD166] to-[#06D6A0] hover:from-[#e6bb54] hover:to-[#05b88a] text-white"
          >
            <FileText className="w-4 h-4 mr-2" />
            Export Financials
          </Button>

          <Button
            onClick={() => navigate('/admin/finance/payments')}
            className="bg-gradient-to-r from-[#EF476F] to-[#FFD166] hover:from-[#d63d5f] hover:to-[#e6bb54] text-white"
            data-testid="quick-action-view-payments"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            View Payments
          </Button>
        </div>
      </Card>

      {/* Auto-refresh indicator */}
      {refreshing && (
        <div className="fixed bottom-4 right-4 bg-white shadow-lg rounded-lg p-3 flex items-center gap-2 border border-gray-200">
          <RefreshCw className="w-4 h-4 animate-spin text-[#00B4D8]" />
          <span className="text-sm text-gray-600">Auto-refreshing...</span>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
